import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Table, useApiQuery, type Column, type CreditInfo, type Plan, type Product } from 'grabbitbot-legacy-ui'
import { paymentClient, statisticsClient } from '../../api'
import type { UsageFee, UsageFeeCoin, UsageFeeQuoteAsset } from '../../api/statisticsTypes'
import { ConfirmButton } from '../admin/adminShared'
import { formatNumberByCoin } from '../trading/coinConfigLabels'
import { BOT_PLAN_IDS, usePaymentActions, usePaymentBasics } from './paymentFlow'
import {
  BillingSummary,
  CreditAmount,
  CreditBuyPanel,
  PaymentMethodSummary,
  PaymentNotice,
  PaymentPage,
  PlanCard,
  ProductCard,
} from './paymentShared'
import { failureText } from '../../api/errorMessages'
import { isCanceledButActive } from './planStatus'

/** The three plan ids whose active subscription counts as "has an active
 * subscription" on the legacy overview (`PaymentComponent.refreshData`) —
 * kept verbatim (round 1 = straight conversion); the same set the plan
 * switch uses (`BOT_PLAN_IDS`). */
const SUBSCRIPTION_PLAN_IDS = BOT_PLAN_IDS

function hasActiveSubscription(plans: Plan[] | null): boolean | null {
  if (!plans) return null
  return plans.some((plan) => {
    if (!plan.id || !SUBSCRIPTION_PLAN_IDS.has(plan.id)) return false
    const sub = plan.subscription
    // a canceled subscription whose paid period still runs counts as active
    // until that date (owner rule 2026-10-10)
    return Boolean(
      sub && ((sub.status === 1 && sub.expiresAt && new Date(sub.expiresAt).getTime() > Date.now()) || isCanceledButActive(sub)),
    )
  })
}

/** Legacy `Math.round(balance * 1e8) / 1e8 < 0` — display rounding only. */
function isNegativeBalance(balance: number | null | undefined): boolean {
  return Math.round(Number(balance ?? 0) * 100000000) / 100000000 < 0
}

function productsAllPaid(products: Product[] | null): boolean | null {
  if (!products) return null
  return products.every((p) => p.onetimeproduct && p.onetimeproduct.status === 1)
}

/**
 * `/payment` — the billing/usage overview (legacy `PaymentComponent`,
 * `src/app/component/payment/`). Status alerts (no active subscription,
 * negative credit balance, unpaid one-time fees), the saved card + "remove
 * payment method", billing summary, credit balance + buy credits, every
 * plan and product with its pay actions, and this / last month's usage fee.
 *
 * Not ported: the previous-month commission block (only rendered in
 * January 2025 — dead code today); the "by coin and strategy" usage-fee
 * breakdown is folded into the by-coin table's strategy columns.
 */
export function PaymentHomePage() {
  const basics = usePaymentBasics()
  const { generation, refetch, method, billing } = basics
  const actions = usePaymentActions()
  const plans = useApiQuery(() => paymentClient.listPlans(), [generation])
  const products = useApiQuery(() => paymentClient.listProducts(), [generation])
  const credits = useApiQuery(() => paymentClient.searchCreditInfos(), [generation])

  const planList = Array.isArray(plans.data) ? plans.data : null
  const productList = Array.isArray(products.data) ? products.data : null
  const creditList: CreditInfo[] = Array.isArray(credits.data) ? credits.data : []
  const activeSub = hasActiveSubscription(planList)
  const negativeCredit = creditList.some((c) => isNegativeBalance(c.balance))
  const allPaid = productsAllPaid(productList)

  return (
    <PaymentPage title="Payment">
      {basics.account.loading && <p>Loading payment information…</p>}
      {basics.account.error && (
        <p className="portal-notice portal-notice--error" role="alert">
          {failureText(basics.account.error, 'Could not load your payment account')}
        </p>
      )}

      {activeSub === false && (
        <div className="portal-notice portal-notice--warning" role="alert">
          <strong>You don&apos;t have an active subscription, and your BOT will be disabled!</strong>
          <p>This can happen, because</p>
          <ul>
            <li>you hasn&apos;t started your subscription yet</li>
            <li>you canceled your subscription</li>
            <li>we couldn&apos;t renew your subscription</li>
          </ul>
          To manage your subscriptions, see the subscription list below, or at the{' '}
          <Link to="/payment/subscriptions">Subscriptions</Link> page.
        </div>
      )}
      {negativeCredit && (
        <div className="portal-notice portal-notice--warning" role="alert">
          <strong>You have negative credit balance in at least one currency, and your BOT will be disabled!</strong> Check
          your credit balances at the <Link to="/payment/credit">Credits</Link> page.
        </div>
      )}
      {allPaid === false && (
        <div className="portal-notice portal-notice--warning" role="alert">
          <strong>You hasn&apos;t paid all onetime fees yet, and your BOT will be disabled!</strong>
          <p>
            Some fees have a grace period starting from the initialization of your BOT, but every fee must be paid before
            this period ends:
          </p>
          <ul>
            <li>One-Time Fee I. ($500) : 30 days</li>
            <li>One-Time Fee II. ($1000) : 90 days</li>
          </ul>
          To manage your onetime fees, see the product list below, or at the <Link to="/payment/products">Products</Link>{' '}
          page. To see when has your BOT been intialized, see the BOT info at the{' '}
          <Link to="/settings/default">Default settings</Link> page.
        </div>
      )}

      {basics.account.data && (
        <>
          <div className="portal-grid-2">
            <section className="portal-card" aria-label="Payment method">
              <h2>Payment method</h2>
              <PaymentMethodSummary method={method} />
              {method && method.setup ? (
                <>
                  <ConfirmButton
                    label="Remove payment method and cancel all active subscriptions"
                    confirmLabel="Yes, remove it"
                    pendingText="Are you sure you want to remove current payment method and cancel all subscriptions?"
                    disabled={actions.isBusy('payment:cancel')}
                    onConfirm={() => void actions.run('payment:cancel', () => paymentClient.cancelPayment(), refetch)}
                  />
                  <PaymentNotice feedback={actions.feedbackFor('payment:cancel')} />
                </>
              ) : null}
              <p>
                <Link to="/payment/method">Manage payment method</Link>
              </p>
              <h3>Billing information</h3>
              <BillingSummary billing={billing} />
            </section>

            <section className="portal-card" aria-label="Credit balance">
              <h2>Credit balance</h2>
              {credits.loading && <p>Loading credit balance…</p>}
              {creditList
                .filter((c) => c.currency === 'credit')
                .map((c) => (
                  <p key={c.currency ?? 'credit'} className="portal-plan-price">
                    <CreditAmount balance={c.balance} currency={c.currency} />
                  </p>
                ))}
              <CreditBuyPanel method={method} billing={billing} actions={actions} onChanged={refetch} />
            </section>
          </div>

          <h2 className="portal-section-title">Subscriptions</h2>
          {plans.loading && <p>Loading subscriptions…</p>}
          {plans.error && <p className="portal-notice portal-notice--error">{failureText(plans.error, 'Could not load subscriptions')}</p>}
          {planList?.map((plan) => (
            <PlanCard
              key={plan.id ?? plan.name}
              plan={plan}
              method={method}
              billing={billing}
              actions={actions}
              onChanged={refetch}
              allPlans={planList}
            />
          ))}

          <h2 className="portal-section-title">Onetime fees</h2>
          {products.loading && <p>Loading products…</p>}
          {products.error && <p className="portal-notice portal-notice--error">{failureText(products.error, 'Could not load products')}</p>}
          <div className="portal-grid-2">
            {productList?.map((product) => (
              <ProductCard
                key={product.id ?? product.name}
                product={product}
                method={method}
                billing={billing}
                actions={actions}
                onChanged={refetch}
              />
            ))}
          </div>
        </>
      )}

      <UsageFeeMonth month={0} title="Usage fee — this month" />
      <UsageFeeMonth month={-1} title="Usage fee — last month" />
    </PaymentPage>
  )
}

interface QuoteRow extends UsageFeeQuoteAsset {
  exchangeId: string
}
interface CoinRow extends UsageFeeCoin {
  exchangeId: string
}

const quoteColumns: Column<QuoteRow>[] = [
  { key: 'exchangeId', label: 'Exchange', align: 'left', sortValue: (r) => r.exchangeId },
  { key: 'coin2', label: 'Asset', align: 'left', sortValue: (r) => r.coin2 },
  { key: 'count', label: 'Transaction count', align: 'right', numeric: true, sortValue: (r) => r.count },
  { key: 'bid', label: 'Sum bid', align: 'right', numeric: true, sortValue: (r) => r.bid, format: (_v, r) => formatNumberByCoin(r.bid, r.coin2) },
  { key: 'systemFee', label: 'System transaction fee', align: 'right', numeric: true, sortValue: (r) => r.systemFee, format: (_v, r) => formatNumberByCoin(r.systemFee, r.coin2) },
  { key: 'strategyFee', label: 'Strategy transaction fee', align: 'right', numeric: true, sortValue: (r) => r.strategyFee, format: (_v, r) => formatNumberByCoin(r.strategyFee, r.coin2) },
  {
    key: 'total',
    label: 'Total transaction fee',
    align: 'right',
    numeric: true,
    sortValue: (r) => r.systemFee + r.strategyFee,
    format: (_v, r) => formatNumberByCoin(r.systemFee + r.strategyFee, r.coin2),
  },
]

const coinColumns: Column<CoinRow>[] = [
  { key: 'exchangeId', label: 'Exchange', align: 'left', sortValue: (r) => r.exchangeId },
  { key: 'coin1', label: 'Base asset', align: 'left', sortValue: (r) => r.coin1 },
  { key: 'coin2', label: 'Quote asset', align: 'left', sortValue: (r) => r.coin2 },
  { key: 'count', label: 'Transaction count', align: 'right', numeric: true, sortValue: (r) => r.count },
  { key: 'bid', label: 'Sum bid', align: 'right', numeric: true, sortValue: (r) => r.bid, format: (_v, r) => formatNumberByCoin(r.bid, r.coin2) },
  { key: 'systemFee', label: 'System transaction fee', align: 'right', numeric: true, sortValue: (r) => r.systemFee, format: (_v, r) => formatNumberByCoin(r.systemFee, r.coin2) },
  { key: 'strategyCount', label: 'Strategy transactions', align: 'right', numeric: true, sortValue: (r) => r.strategyCount },
  { key: 'strategyFee', label: 'Strategy transaction fee', align: 'right', numeric: true, sortValue: (r) => r.strategyFee, format: (_v, r) => formatNumberByCoin(r.strategyFee, r.coin2) },
  {
    key: 'total',
    label: 'Total transaction fee',
    align: 'right',
    numeric: true,
    sortValue: (r) => r.systemFee + r.strategyFee,
    format: (_v, r) => formatNumberByCoin(r.systemFee + r.strategyFee, r.coin2),
  },
]

function UsageFeeMonth({ month, title }: { month: number; title: string }) {
  const { data, loading, error } = useApiQuery<UsageFee>(() => statisticsClient.usageFeeMonth(month), [month])
  const [showCoins, setShowCoins] = useState(false)
  const quoteRows: QuoteRow[] = (data?.exchanges ?? []).flatMap((ex) => ex.items.map((q) => ({ ...q, exchangeId: ex.exchangeId })))
  const coinRows: CoinRow[] = (data?.exchanges ?? []).flatMap((ex) =>
    ex.items.flatMap((q) => q.items.map((c) => ({ ...c, exchangeId: ex.exchangeId }))),
  )
  return (
    <section className="portal-card" aria-label={title}>
      <h2>{title}</h2>
      {data && (
        <p className="portal-text-muted">
          {data.fromDate} – {data.toDate}
        </p>
      )}
      {loading && <p>Loading usage fee…</p>}
      {error && <p className="portal-notice portal-notice--error">{failureText(error, 'Could not load the usage fee')}</p>}
      {data && quoteRows.length === 0 && <p className="portal-text-muted">No usage fee in this period.</p>}
      {quoteRows.length > 0 && (
        <div className="portal-table-scroll">
          <Table columns={quoteColumns} rows={quoteRows} rowKey={(r) => `${r.exchangeId}:${r.coin2}`} />
        </div>
      )}
      {coinRows.length > 0 && (
        <>
          <button type="button" onClick={() => setShowCoins((v) => !v)}>
            {showCoins ? 'Hide details by coin' : 'Show details by coin'}
          </button>
          {showCoins && (
            <div className="portal-table-scroll">
              <Table columns={coinColumns} rows={coinRows} rowKey={(r) => `${r.exchangeId}:${r.coin1}:${r.coin2}`} />
            </div>
          )}
        </>
      )}
    </section>
  )
}
